import AttachFileIcon from '@mui/icons-material/AttachFile'
import ClearIcon from '@mui/icons-material/Clear'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import EditNoteIcon from '@mui/icons-material/EditNote'
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Badge, Box, Button, Checkbox, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import CustomBackdrop from 'common/components/CustomBackdrop'
import Table from 'common/components/Table'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { ModalTypes } from 'common/hooks/useCustomModal'
import { DocIcon } from 'icons/DocIcon'
import i18n from 'lang/idiomas'
import { useContext, useEffect, useState } from 'react'
import FilesHelper from 'src/common/libs/Files'
import { GlobalVariablesContext } from 'src/common/providers/GlobalVariablesProvider'
import DocumentViewer from 'src/features/documentos/components/DocumentViewer'
import {
  enrichFileForSigning,
  getActionableFile,
  getAdjuntosCount,
  getNumeroExpediente,
} from './Constants'
import DocumentDetailDialog from './DocumentDetailDialog'
import DocumentDialogs from './DocumentDialogs'

const isDocFirmable = (doc) =>
  doc.permitir_adjuntar_documentos === 'N' &&
  (doc.ficheros_a_firmar ?? []).some((f) => f.puede_firmar === 'S')

const FirmaDocumentosTableView = ({
  documentos,
  loading,
  selectedDocs,
  setSelectedDocs,
  setTrigger,
  setErrorStatus,
  statusColorMap,
  tipoDocColorMap,
}) => {
  const { handleModal } = useContext(CustomModalContext)

  const { IS_GOTTENBERG_ACTIVE } = useContext(GlobalVariablesContext)

  const [activeDoc, setActiveDoc] = useState(null)
  const [rowAction, setRowAction] = useState('')
  const [rowModalOpen, setRowModalOpen] = useState(false)
  const [detailDoc, setDetailDoc] = useState(null)
  const [openBackdrop, setOpenBackdrop] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    setCurrentPage(1)
  }, [documentos])

  const firmableDocs = documentos.filter(isDocFirmable)
  const allFirmableSelected =
    firmableDocs.length > 0 && firmableDocs.every((d) => selectedDocs.has(d.docId))
  const someFirmableSelected =
    firmableDocs.some((d) => selectedDocs.has(d.docId)) && !allFirmableSelected

  const handleSelectAll = () => {
    if (allFirmableSelected) {
      setSelectedDocs(new Set())
    } else {
      setSelectedDocs(new Set(firmableDocs.map((d) => d.docId)))
    }
  }

  const handleDocumentSelect = (doc) => {
    const next = new Set(selectedDocs)
    if (next.has(doc.docId)) {
      next.delete(doc.docId)
    } else {
      next.add(doc.docId)
    }
    setSelectedDocs(next)
  }

  const verDocumento = async (fichero) => {
    handleModal({
      enabled: true,
      closable: true,
      type: ModalTypes.Custom,
      content: (
        <DocumentViewer
          document_url={`/api/v1/documentos/descargaFichero/${fichero.token_acceso}/`}
          forced={false}
          title={fichero.nombre_archivo}
        />
      ),
    })
  }

  const descargarDocumento = async (fichero) => {
    setOpenBackdrop(true)
    await FilesHelper.downloadFile(
      null,
      null,
      `/api/v1/documentos/descargaFichero/${fichero.token_acceso}/`,
    ).finally(() => {
      setOpenBackdrop(false)
    })
  }

  const openRowDialog = (fichero, action) => {
    setActiveDoc(fichero)
    setRowAction(action)
    setRowModalOpen(true)
  }

  const handleFileAction = (enrichedFile, action) => {
    setActiveDoc(enrichedFile)
    setRowAction(action)
    setRowModalOpen(true)
  }

  const fields = [
    {
      title: (
        <Checkbox
          size="small"
          checked={allFirmableSelected}
          indeterminate={someFirmableSelected}
          onChange={handleSelectAll}
          sx={{ my: -1 }}
        />
      ),
      field: '_checkbox',
      cellWidth: '50px',
      forceWidth: true,
      customGetter: (item) =>
        isDocFirmable(item) ? (
          <Checkbox
            size="small"
            checked={selectedDocs.has(item.docId)}
            onChange={() => handleDocumentSelect(item)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : null,
    },
    {
      title: 'Estado',
      field: 'descrip_status',
      cellWidth: '120px',
      forceWidth: true,
      customGetter: (item) => {
        const colors = statusColorMap?.[`${item.codigo_tipo_doc}-${item.codigo_status}`]
        return (
          <Chip
            label={colors?.label ?? item.codigo_status}
            size="small"
            sx={colors ? { bgcolor: colors.bgcolor, color: colors.color } : undefined}
          />
        )
      },
    },
    {
      title: 'Documento',
      field: 'descripcion',
      cellWidth: '1fr',
      customGetter: (item) => {
        const docColor = tipoDocColorMap?.[item.codigo_tipo_doc]
        const desc = item.descripcion ?? ''
        const truncatedDesc = desc.length > 40 ? desc.substring(0, 40) + '...' : desc
        const numeroExpediente = getNumeroExpediente(item)
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 }}>
            <DocIcon
              kind={item.codigo_tipo_doc?.toLowerCase()}
              color={docColor}
              size={36}
              decorative
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {numeroExpediente && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {i18n.t('exped', { defaultValue: 'Expediente:' })} {numeroExpediente}
                </Typography>
              )}
              <Tooltip title={desc.length > 40 ? desc : ''} arrow>
                <Typography variant="body1" sx={{ lineHeight: 1.3 }}>
                  {truncatedDesc}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        )
      },
    },
    {
      title: '',
      field: '_adjuntos',
      cellWidth: '50px',
      forceWidth: true,
      customGetter: (item) => {
        const count = getAdjuntosCount(item)
        if (count === 0) return null
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Badge badgeContent={count} color="secondary">
              <AttachFileIcon fontSize="small" color="action" />
            </Badge>
          </Box>
        )
      },
    },
    {
      title: 'Fecha',
      field: 'fecha_documento',
      cellWidth: '120px',
      forceWidth: true,
      customGetter: (item) => {
        const fecha = item.ficheros_a_firmar?.[0]?.fecha_documento
        return fecha
          ? new Date(fecha).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : ''
      },
    },
    {
      title: 'Acciones',
      field: 'acciones',
      cellWidth: '250px',
      forceWidth: true,
      customGetter: (item) => {
        const actionFile = getActionableFile(item)
        if (!actionFile) return null
        const enrichedFile = enrichFileForSigning(actionFile, item)
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            {(IS_GOTTENBERG_ACTIVE || actionFile.mime_type === 'application/pdf') && (
              <Tooltip title="Ver" placement="top">
                <IconButton size="medium" onClick={() => verDocumento(actionFile)}>
                  <VisibilityIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Descargar" placement="top">
              <IconButton size="medium" onClick={() => descargarDocumento(actionFile)}>
                <CloudDownloadIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
            {actionFile.puede_firmar === 'S' && (
              <>
                <Tooltip title="Observaciones" placement="top">
                  <IconButton size="medium" onClick={() => openRowDialog(enrichedFile, 'O')}>
                    <EditNoteIcon fontSize="medium" sx={{ color: '#426bba' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Firmar" placement="top">
                  <IconButton size="medium" onClick={() => openRowDialog(enrichedFile, 'F')}>
                    <FileDownloadDoneIcon fontSize="medium" sx={{ color: '#7FB77E' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rechazar" placement="top">
                  <IconButton size="medium" onClick={() => openRowDialog(enrichedFile, 'R')}>
                    <ClearIcon fontSize="medium" sx={{ color: '#E64848' }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <>
      <CustomBackdrop
        open={openBackdrop}
        text={i18n.t('descargandoArchivo', { defaultValue: 'Descargando archivo' })}
      />
      {selectedDocs.size >= 1 && (
        <Box sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const selectedDocuments = documentos.filter((d) => selectedDocs.has(d.docId))
              const signableFiles = selectedDocuments.flatMap((doc) =>
                (doc.ficheros_a_firmar ?? [])
                  .filter((f) => f.puede_firmar === 'S')
                  .map((f) => enrichFileForSigning(f, doc)),
              )
              setActiveDoc(signableFiles)
              setRowAction('F')
              setRowModalOpen(true)
            }}
          >
            Firmar {selectedDocs.size} documento{selectedDocs.size > 1 ? 's' : ''} seleccionado
            {selectedDocs.size > 1 ? 's' : ''}
          </Button>
        </Box>
      )}

      <Table
        dataSource={documentos}
        fields={fields}
        loading={loading}
        onRowClick={({ row }) => setDetailDoc(row)}
        page={currentPage}
        onPageChanged={setCurrentPage}
        count={documentos.length}
        rowsPerPage={12}
        emptyText="No hay documentos"
      />

      {/* Per-row DocumentDialogs */}
      {activeDoc && rowAction && (
        <DocumentDialogs
          setErrorStatus={setErrorStatus}
          docData={Array.isArray(activeDoc) ? activeDoc : [activeDoc]}
          modalOpen={rowModalOpen}
          setModalOpen={(open) => {
            setRowModalOpen(open)
            if (!open) {
              setActiveDoc(null)
              setRowAction('')
            }
          }}
          action={rowAction}
          setTrigger={setTrigger}
        />
      )}

      {/* Detail Dialog */}
      <DocumentDetailDialog
        document={detailDoc}
        open={Boolean(detailDoc)}
        onClose={() => setDetailDoc(null)}
        onFileAction={handleFileAction}
        setTrigger={setTrigger}
        statusColorMap={statusColorMap}
        tipoDocColorMap={tipoDocColorMap}
      />
    </>
  )
}

export default FirmaDocumentosTableView
